import { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import ConfettiCelebration from "@/components/ConfettiCelebration";

interface ResearchPartnershipCardProps {
  userId: string;
  initialConsent: boolean;
  isLoading: boolean;
}

const ResearchPartnershipCard = ({ userId, initialConsent, isLoading }: ResearchPartnershipCardProps) => {
  const [consent, setConsent] = useState(initialConsent);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    setConsent(initialConsent);
  }, [initialConsent]);

  const handleToggle = async (checked: boolean) => {
    setConsent(checked);
    try {
      await supabase
        .from("profiles")
        .update({ research_consent: checked } as any)
        .eq("user_id", userId);

      if (checked) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        toast.success("🎉 Welcome to the Research Partnership!", {
          description: "You're now helping shape the future of GreyRock AI. Thank you!",
        });
      } else {
        toast("Research contribution disabled.", {
          description: "Your data will no longer be included. You can re-enable anytime.",
        });
      }
    } catch {
      setConsent(!checked);
      toast.error("Failed to update preference");
    }
  };

  return (
    <>
      {showConfetti && <ConfettiCelebration trigger={showConfetti} onComplete={() => setShowConfetti(false)} />}
      <div className="relative group">
        {/* Gradient border wrapper */}
        <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-cyan/40 via-cyan-glow/20 to-cyan/10 opacity-70 group-hover:opacity-100 transition-opacity" />

        <div className="relative glass-card p-6 rounded-2xl overflow-hidden">
          {/* Subtle background glow */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-cyan/5 rounded-full blur-3xl pointer-events-none" />

          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-cyan/20 to-cyan-glow/10 border border-cyan/20">
              <Sparkles className="w-5 h-5 text-cyan" />
            </div>
            <div>
              <h2 className="font-semibold text-lg text-foreground">Research Partnership</h2>
              <p className="text-xs text-cyan/70 font-medium tracking-wide uppercase">Early Access Program</p>
            </div>
          </div>

          {/* Value proposition */}
          <div className="mb-5">
            <p className="text-base font-semibold text-foreground mb-2">
              Help Shape the Future of Vibe-Coding.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Opt-in to share anonymized usage data. Early contributors get{" "}
              <span className="text-cyan font-medium">priority access to next-gen experimental models</span>{" "}
              and a{" "}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan/10 border border-cyan/20 text-cyan text-xs font-semibold">
                <Sparkles className="w-3 h-3" />
                Founding Researcher
              </span>{" "}
              badge on your profile.
            </p>
          </div>

          {/* Toggle row */}
          <div className={cn(
            "flex items-center justify-between p-4 rounded-xl transition-colors",
            consent ? "bg-cyan/10 border border-cyan/20" : "bg-charcoal-medium"
          )}>
            <div>
              <p className="font-medium text-foreground text-sm">
                {consent ? "You're a Founding Researcher ✨" : "Contribute Anonymized Data"}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Data is fully encrypted and anonymized
              </p>
            </div>
            <Switch
              checked={consent}
              onCheckedChange={handleToggle}
              disabled={isLoading}
              className="data-[state=checked]:bg-cyan"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ResearchPartnershipCard;
